import {createSlice, PayloadAction} from '@reduxjs/toolkit';

import {Subscription} from '~shared/schema/wiki/actions/subscriptions';

import {
    BasePanelState,
    init,
    loadNextPage,
    makeAddItems,
    makeChangeOrder,
    makeReset,
    makeSetItems,
    setError,
    setLoading,
    setLoadingMore,
    setQueryFilter,
} from '../common';

import {createSubscription, deleteSubscriptions} from './thunks';
import {SubscriptionTypes} from './types';

export type SubscriptionsBookmarksState = BasePanelState<
    SubscriptionItem,
    {type: SubscriptionTypes}
>;

export type SubscriptionItem = ReturnType<typeof toTableData>;

const initialState: SubscriptionsBookmarksState = {
    items: [],
    isFirstPage: true,
    nextCursor: null,
    loading: false,
    loadingMore: false,
    isEmpty: true,
    filters: {
        query: '',
        type: SubscriptionTypes.All,
    },
    orderBy: 'createdAt',
    orderDirection: 'desc',
};

export const {actions, reducer} = createSlice({
    name: 'subscriptions',
    initialState,
    reducers: {
        init,
        reset: makeReset(initialState),
        loadNextPage,
        changeOrder: makeChangeOrder('createdAt'),
        setItems: makeSetItems(toTableData),
        addItems: makeAddItems(toTableData),
        setLoading,
        setLoadingMore,
        setError,
        setQueryFilter,

        setTypeFilter(state, action: PayloadAction<SubscriptionTypes>) {
            state.nextCursor = null;
            state.isFirstPage = true;
            state.filters.type = action.payload;
        },
    },
    extraReducers: (builder) =>
        builder
            .addCase(createSubscription.fulfilled, (state, action) => {
                const newItem = toTableData(action.payload);

                const item = state.items.find((item) => newItem.slug === item.slug);

                if (item) {
                    item.isRemoved = false;
                    return;
                }

                // TODO should be unreal case right now
                // gracefully degrade if we reach this code
                // (p.s. it may break the sorting)
                state.items.unshift(newItem);
            })

            .addCase(deleteSubscriptions.fulfilled, (state, action) => {
                const pagesToDelete = action.meta.arg.pages.map((identifier) => ({
                    id: undefined,
                    slug: undefined,
                    ...identifier,
                }));

                const newItems: SubscriptionItem[] = [];

                state.items.forEach((item) => {
                    const needToDelete = pagesToDelete.some((identifier) => {
                        return identifier.id === item.pageId || identifier.slug === item.slug;
                    });

                    if (needToDelete) {
                        item.isRemoved = true;
                    }

                    // remove subscription from the list
                    // if the page was deleted
                    if (needToDelete && !item.isActive) {
                        return;
                    }

                    newItems.push(item);
                });

                state.items = newItems;
            }),
});

function toTableData(subscription: Subscription) {
    return {
        id: subscription.id,
        author: subscription.page.owner,
        title: subscription.page.title,
        slug: subscription.page.slug,
        pageId: subscription.page.id,
        pageModifiedAt: subscription.page.modified_at,
        pageCreatedAt: subscription.page.created_at,
        isCluster: subscription.is_cluster,
        isActive: subscription.page.is_active,
        isRemoved: false,
    };
}
